/* ========================================
   Artist Profile — Shared Utilities
   ======================================== */

function formatNumber(num) {
  if (num == null) return "0";
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 10000) return (num / 1000).toFixed(1) + "K";
  return num.toLocaleString();
}

function isVideo(url) {
  if (!url) return false;
  const lower = url.toLowerCase().split("?")[0];
  return (
    lower.endsWith(".mp4") || lower.endsWith(".webm") || lower.endsWith(".mov")
  );
}

function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text || "";
  return div.innerHTML;
}

function toHandle(name) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/**
 * Insert Cloudinary on-the-fly transforms into a Cloudinary URL.
 * Non-Cloudinary URLs are returned unchanged.
 */
function cloudinaryTransform(url, transforms) {
  if (typeof url !== "string" || !url || !url.includes("cloudinary.com"))
    return url;
  const parts = url.split("/upload/");
  if (parts.length !== 2) return url;
  return parts[0] + "/upload/" + transforms + "/" + parts[1];
}

/**
 * Mirror of klydo-api's CloudinaryClient.getScaledUrl — produces a URL that
 * Cloudinary will scale to `width` and serve at auto-quality. Pass extra
 * transforms (e.g. 'f_webp') as the third argument when needed.
 */
function getScaledCloudinaryUrl(url, width, additionalTransforms) {
  const baseTransforms = "so_auto,w_" + width + ",c_scale,q_auto";
  const transforms = additionalTransforms
    ? baseTransforms + "," + additionalTransforms
    : baseTransforms;
  return cloudinaryTransform(url, transforms);
}

function extractDominantColor(imageUrl) {
  return new Promise(function (resolve, reject) {
    const image = new Image();
    image.crossOrigin = "Anonymous";
    image.onload = function () {
      try {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (!context) {
          reject(new Error("No canvas context"));
          return;
        }
        const sampleSize = 50;
        canvas.width = sampleSize;
        canvas.height = sampleSize;
        context.drawImage(image, 0, 0, sampleSize, sampleSize);
        const pixelData = context.getImageData(
          0,
          0,
          sampleSize,
          sampleSize,
        ).data;
        let totalRed = 0,
          totalGreen = 0,
          totalBlue = 0,
          pixelCount = 0;
        for (let offset = 0; offset < pixelData.length; offset += 4) {
          totalRed += pixelData[offset];
          totalGreen += pixelData[offset + 1];
          totalBlue += pixelData[offset + 2];
          pixelCount++;
        }
        const avgRed = Math.round(totalRed / pixelCount);
        const avgGreen = Math.round(totalGreen / pixelCount);
        const avgBlue = Math.round(totalBlue / pixelCount);
        resolve("rgb(" + avgRed + "," + avgGreen + "," + avgBlue + ")");
      } catch (error) {
        reject(error);
      }
    };
    image.onerror = function () {
      reject(new Error("Failed to load image"));
    };
    image.src = imageUrl;
  });
}

(function attachKlydoImageProtection() {
  const PROTECTED_IMAGE_SELECTOR = [
    ".all-klydos__card-image",
    ".artist-klydo-card__media-wrap img",
    ".artist-klydo-card__media-wrap video",
    ".klydo-modal__clock img",
    ".klydo-modal__clock video",
    ".all-collections__card-image",
    ".artist-collection-card__image",
    ".cs-double-image__item img",
    ".cs-media-text__media > img",
    ".cs-media-text__media > video",
    ".cs-full-width__media > img",
    ".cs-full-width__media > video",
  ].join(",");

  document.addEventListener("contextmenu", function (event) {
    const target = event.target;
    if (target && target.closest && target.closest(PROTECTED_IMAGE_SELECTOR)) {
      event.preventDefault();
    }
  });
  document.addEventListener("dragstart", function (event) {
    const target = event.target;
    if (target && target.closest && target.closest(PROTECTED_IMAGE_SELECTOR)) {
      event.preventDefault();
    }
  });
})();

function wireBreadcrumbReplace(targetUrl, linkElementIds) {
  const navigateToTarget = (event) => {
    event.preventDefault();
    window.location.replace(targetUrl);
  };
  linkElementIds.forEach((elementId) => {
    const element = document.getElementById(elementId);
    if (!element) return;
    element.href = targetUrl;
    element.addEventListener("click", navigateToTarget);
  });
}

/* ---- Social Icons ---- */
const socialIcons = {
  website:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>',
  globe:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>',
  email:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 4l-10 8L2 4"/></svg>',
  instagram:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/></svg>',
  facebook:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>',
  twitter:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/></svg>',
  x: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
  youtube:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22.54 6.42a2.78 2.78 0 00-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 00-1.94 2A29 29 0 001 11.75a29 29 0 00.46 5.33A2.78 2.78 0 003.4 19.1c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 001.94-2 29 29 0 00.46-5.25 29 29 0 00-.46-5.33z"/><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/></svg>',
  tiktok:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.55a8.19 8.19 0 004.77 1.52V6.69h-1z"/></svg>',
  linkedin:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>',
  pinterest:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.08 3.15 9.42 7.6 11.18-.1-.94-.2-2.38.04-3.4.22-.92 1.4-5.9 1.4-5.9s-.36-.72-.36-1.78c0-1.66.97-2.9 2.17-2.9 1.02 0 1.52.77 1.52 1.7 0 1.03-.66 2.58-1 4.01-.28 1.2.6 2.17 1.78 2.17 2.13 0 3.77-2.25 3.77-5.5 0-2.87-2.06-4.88-5.01-4.88-3.41 0-5.42 2.56-5.42 5.2 0 1.03.4 2.13.89 2.73.1.12.11.22.08.34l-.33 1.36c-.05.22-.18.27-.4.16-1.5-.7-2.43-2.88-2.43-4.64 0-3.78 2.75-7.25 7.92-7.25 4.16 0 7.4 2.97 7.4 6.93 0 4.13-2.6 7.46-6.22 7.46-1.21 0-2.36-.63-2.75-1.38l-.75 2.85c-.27 1.04-1 2.35-1.49 3.15C9.57 23.81 10.75 24 12 24c6.63 0 12-5.37 12-12S18.63 0 12 0z"/></svg>',
};

const SOCIAL_NETWORKS_USING_WEB_ICON = ["website", "web", "globe"];

function getSocialIcon(network) {
  const key = (network || "").toLowerCase().trim();
  const webIconUrl = window.socialIconUrls && window.socialIconUrls.web;
  if (webIconUrl && SOCIAL_NETWORKS_USING_WEB_ICON.indexOf(key) !== -1) {
    return '<img src="' + escapeHtml(webIconUrl) + '" alt="">';
  }
  return socialIcons[key] || socialIcons.globe;
}

function buildSocialHtml(artist, cssClass) {
  let html = "";
  if (artist.email) {
    html +=
      '<a class="' +
      cssClass +
      '" href="mailto:' +
      escapeHtml(artist.email) +
      '" title="Email">' +
      getSocialIcon("email") +
      "</a>";
  }
  if (artist.socialMedia && artist.socialMedia.length > 0) {
    artist.socialMedia.forEach(function (social) {
      html +=
        '<a class="' +
        cssClass +
        '" href="' +
        escapeHtml(social.link) +
        '" target="_blank" rel="noopener" title="' +
        escapeHtml(social.network) +
        '">' +
        getSocialIcon(social.network) +
        "</a>";
    });
  }
  return html;
}

/* ---- Share Button ---- */
const SHARE_BUTTON_COPIED_HTML = "Copied!";

function initShare(button) {
  const defaultHtml = button.innerHTML;
  button.addEventListener("click", function () {
    navigator.clipboard.writeText(window.location.href).then(function () {
      button.classList.add("artist-hero__share--copied");
      button.innerHTML = SHARE_BUTTON_COPIED_HTML;
      setTimeout(function () {
        button.classList.remove("artist-hero__share--copied");
        button.innerHTML = defaultHtml;
      }, 2000);
    });
  });
}

/* ---- Scroll Reveal ---- */
function initScrollReveal() {
  const sections = document.querySelectorAll(".artist-reveal");
  if (!("IntersectionObserver" in window)) {
    sections.forEach(function (section) {
      section.classList.add("artist-reveal--visible");
    });
    return;
  }
  const observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add("artist-reveal--visible");
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1 },
  );
  sections.forEach(function (section) {
    observer.observe(section);
  });
}
